import {OtaMappingRules} from "crqlar-react-common/dist/models/integrations/otaMappingRules.model";
import Property from "crqlar-react-common/dist/components/Property";
import {Autocomplete, IconButton, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import Button from "@mui/material/Button";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {useEffect, useMemo, useState} from "react";
import {debounce, range} from "lodash";
import {useQuery} from "react-query";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useTranslation} from "react-i18next";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";

interface Props {
    value?: OtaMappingRules
    onChange: (otaMappingRules: OtaMappingRules) => void
}

const OtaMappingRulesForm = (props: Props) => {

    const {t} = useTranslation()
    const sectors: Sector[] = useAllSectors()
    const [tmpInterestName, setTmpInterestName] = useState('')
    const [tmpInterestCode, setTmpInterestCode] = useState('')

    const updateOtaMappingRules = (field: keyof OtaMappingRules, value: any) => {
        props.onChange({
            ...props.value,
            [field]: value
        })
    }

    const [resourceDebouncedQuery, setResourceDebouncedQuery] = useState('')

    const searchResourcesQuery = useQuery(['allResources', resourceDebouncedQuery], async () => {
        const response = await ResourceService.getAllResources({
            name: resourceDebouncedQuery ? [resourceDebouncedQuery] : undefined,
            limit: {
                offset: 0,
                limit: 50,
                sort: [{field: "firstName", direction: "ASC"}]
            }
        })

        return response.content || []
    })

    const resourceDebouncedSearch = useMemo(() =>
            debounce((query) => {
                setResourceDebouncedQuery(query)
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            resourceDebouncedSearch.cancel();
        };
    }, [resourceDebouncedSearch])

    const ageCategories = useAllAgeCategories()

    const ageQualifyingCodes = range(1, 19).map(i => i.toString())
    const ageQualifyingCodeNames: any = {
        '1': 'Over 21',
        '2': 'Over 65',
        '3': 'Under 2',
        '4': 'Under 12',
        '5': 'Under 17',
        '6': 'Under 21',
        '7': 'Infant',
        '8': 'Child',
        '9': 'Teenager',
        '10': 'Adult',
        '11': 'Senior',
        '12': 'Additional occupant with adult',
        '13': 'Additional occupant without adult',
        '14': 'Free child',
        '15': 'Free adult',
        '16': 'Young driver',
        '17': 'Younger driver',
        '18': 'Under 10',
        '19': 'Junior'
    }

    return (
        <AdditionalProperties title={t('OTA Mapping rules')} variant={'outlined'}>
            <Property
                title={t('Age category code to age category ID')}
                tooltip={t('Maps incoming OTA age qualifying codes to local age categories.')}
            >
                <Stack gap={1} width={'100%'}>
                    {
                        Object.keys(props.value?.ageQualifyingCodeToAgeCategoryId || {}).map(k => {
                            return (
                                <Stack
                                    direction={'row'}
                                    key={k}
                                    width={'100%'}
                                    gap={1}
                                >
                                    <Autocomplete
                                        options={ageQualifyingCodes}
                                        getOptionLabel={(option: string) => ageQualifyingCodeNames[option] + " (" + option + ")"}
                                        value={k}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size={'small'}
                                            />
                                        )}
                                        fullWidth
                                        onChange={(e, value) => {
                                            if (value) {
                                                const newAssignements = {...props.value?.ageQualifyingCodeToAgeCategoryId}
                                                delete newAssignements['']
                                                newAssignements[value] = undefined

                                                updateOtaMappingRules('ageQualifyingCodeToAgeCategoryId', newAssignements)
                                            }
                                        }}
                                    />

                                    <Autocomplete
                                        id="age-category-find"
                                        value={ageCategories.find(ac => ac.id === props.value?.ageQualifyingCodeToAgeCategoryId?.[k]) || null}
                                        getOptionLabel={(option) => option.name || ''}
                                        options={ageCategories}
                                        fullWidth
                                        onChange={(e, value) => {
                                            if (value) {
                                                const newAssignements = {...props.value?.ageQualifyingCodeToAgeCategoryId}
                                                newAssignements[k] = value.id
                                                updateOtaMappingRules('ageQualifyingCodeToAgeCategoryId', newAssignements)
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size={'small'}
                                                fullWidth
                                            />
                                        )}
                                    />

                                    <IconButton
                                        onClick={() => {
                                            const newAssignements = {...props.value?.ageQualifyingCodeToAgeCategoryId}
                                            delete newAssignements[k]
                                            updateOtaMappingRules('ageQualifyingCodeToAgeCategoryId', newAssignements)
                                        }}
                                    >
                                        <DeleteIcon sx={{
                                            width: '24px',
                                            height: '24px',
                                            color: CommonThemeColors.sys.error.main
                                        }}/>
                                    </IconButton>

                                </Stack>
                            )
                        })
                    }

                    <Button
                        onClick={() => {
                            updateOtaMappingRules('ageQualifyingCodeToAgeCategoryId', {
                                ...props.value?.ageQualifyingCodeToAgeCategoryId,
                                ['']: undefined
                            })
                        }}
                    >
                        {t('Add new mapping')}
                    </Button>
                </Stack>


            </Property>

            <Property
                title={t('Accommodation sector')}
                tooltip={t('Sector used when OTA room and accommodation data creates or updates reservations.')}
            >
                <Autocomplete
                    id={'sectors'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === props.value?.accommodationSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateOtaMappingRules('accommodationSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Auto create resource for a room if not found')}
                tooltip={t('Creates missing room resources automatically when an OTA import references an unknown room.')}
            >
                <Switch
                    id={'auto-create-resource-if-not-found'}
                    checked={props.value?.autoCreateResourceIfNotFound}
                    onChange={(e) => updateOtaMappingRules('autoCreateResourceIfNotFound', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Store all additional resource info')}
                tooltip={t('Stores extra room/resource metadata from OTA imports on the reservation resource data.')}
            >
                <Switch
                    id={'pick-additional-resource-info'}
                    checked={props.value?.pickAdditionalResourceInfo}
                    onChange={(e) => updateOtaMappingRules('pickAdditionalResourceInfo', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Interest code to value')}
                tooltip={t('Maps incoming OTA interest codes to readable preference or interest values.')}
            >
                <Stack gap={1} width={'100%'}>
                    {
                        Object.keys(props.value?.interestCodeToValue || {}).map((k, index) => {
                            return (
                                <Stack
                                    direction={'row'}
                                    key={index}
                                    width={'100%'}
                                    gap={1}
                                >
                                    <TextField
                                        value={k}
                                        variant={'outlined'}
                                        size={'small'}
                                        sx={{width: '50%'}}
                                        disabled
                                    />
                                    <TextField
                                        value={props.value?.interestCodeToValue?.[k]}
                                        onChange={(e) => {
                                            const newAssignements = {...props.value?.interestCodeToValue}
                                            newAssignements[k] = e.target.value
                                            updateOtaMappingRules('interestCodeToValue', newAssignements)
                                        }}
                                        variant={'outlined'}
                                        size={'small'}
                                        sx={{width: '50%'}}
                                    />

                                    <IconButton
                                        onClick={() => {
                                            const newAssignements = {...props.value?.interestCodeToValue}
                                            delete newAssignements[k]
                                            updateOtaMappingRules('interestCodeToValue', newAssignements)
                                        }}
                                    >
                                        <DeleteIcon sx={{
                                            width: '24px',
                                            height: '24px',
                                            color: CommonThemeColors.sys.error.main
                                        }}/>
                                    </IconButton>

                                </Stack>
                            )
                        })
                    }

                    <Stack
                        direction={'row'}
                        width={'100%'}
                        gap={1}
                        mt={3}
                    >
                        <TextField
                            value={tmpInterestCode}
                            variant={'outlined'}
                            size={'small'}
                            sx={{width: '50%'}}
                            onChange={(e) => setTmpInterestCode(e.target.value)}
                            label={t('Interest code')}
                        />
                        <TextField
                            value={tmpInterestName}
                            onChange={(e) => setTmpInterestName(e.target.value)}
                            variant={'outlined'}
                            size={'small'}
                            sx={{width: '50%'}}
                            label={t('Interest name')}
                        />

                        <Button
                            onClick={() => {
                                updateOtaMappingRules('interestCodeToValue', {
                                    ...props.value?.interestCodeToValue,
                                    [tmpInterestCode]: tmpInterestName
                                })
                                setTmpInterestCode('')
                                setTmpInterestName('')
                            }}
                        >
                            {t('Add')}
                        </Button>

                    </Stack>


                </Stack>


            </Property>

        </AdditionalProperties>
    )

}

export default OtaMappingRulesForm;
